import { Card, CardContent } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Progress } from '@/common/ui/progress'
import useQueryGetPlayerReturnLoss from '@/modules/manage-player/api/hooks/useQueryGetPlayerReturnLoss'
import {
	formatGameTypeToThai,
	formatCalculateIntervalToThai,
	formatCreditsAmount,
} from '../../utils/gameTypeLabels'
import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import { cn } from '@/common/lib/utils'

type PlayerReturnLossInfoProps = {
	playerId: string
	embedded?: boolean
}

const shellClass = (embedded: boolean) =>
	cn(embedded ? 'space-y-4' : 'rounded-lg border bg-card p-4 shadow-sm sm:p-6')

export default function PlayerReturnLossInfo({
	playerId,
	embedded = false,
}: PlayerReturnLossInfoProps) {
	const { data, isLoading, isError } = useQueryGetPlayerReturnLoss(playerId)

	if (isLoading) {
		return (
			<div className={shellClass(embedded)}>
				<Skeleton className="mb-4 h-8 w-48" />
				<div className="space-y-3">
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
				</div>
			</div>
		)
	}

	if (isError || !data) {
		return (
			<div className={shellClass(embedded)}>
				{!embedded && (
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						คืนยอดเสีย
					</h2>
				)}
				<p className="mt-2 text-sm text-muted-foreground">
					ไม่สามารถโหลดข้อมูลได้ หรือสมาชิกไม่มีคืนยอดเสียตามยศ
				</p>
			</div>
		)
	}

	const { info, calculation } = data
	const creditEntries = Object.entries(info.creditRatio ?? {})
	const progressPct = Math.min(
		100,
		info.minimumLossAmount > 0
			? (Math.max(0, calculation.totalReturnLossAmount) /
					info.minimumLossAmount) *
					100
			: 0,
	)

	return (
		<div className={shellClass(embedded)}>
			{embedded ? (
				<div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
					<p className="text-sm text-muted-foreground">{info.name}</p>
					<Badge
						variant={info.isActive ? 'default' : 'secondary'}
						className="w-fit shrink-0"
					>
						{info.isActive ? 'เปิดใช้งาน' : 'ปิด'}
					</Badge>
				</div>
			) : (
				<div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
							คืนยอดเสีย
						</h2>
						<p className="text-sm text-muted-foreground sm:text-base">
							{info.name}
						</p>
					</div>
					<Badge variant={info.isActive ? 'default' : 'secondary'}>
						{info.isActive ? 'เปิดใช้งาน' : 'ปิด'}
					</Badge>
				</div>
			)}

			<div className="mb-4 grid gap-3 text-sm sm:grid-cols-2">
				<div className="rounded-lg border bg-muted/30 p-3">
					<p className="text-muted-foreground">รอบคำนวณ</p>
					<p className="font-medium">
						{formatCalculateIntervalToThai(String(info.calculateInterval))}
					</p>
				</div>
				<div className="rounded-lg border bg-muted/30 p-3">
					<p className="text-muted-foreground">จ่ายครั้งถัดไป (โดยประมาณ)</p>
					<p className="font-medium">
						{format(parseISO(info.nextCalculateAt), 'd MMM yyyy HH:mm', {
							locale: th,
						})}
					</p>
				</div>
				<div className="rounded-lg border bg-muted/30 p-3">
					<p className="text-muted-foreground">ยอดเสียรวมในช่วงปัจจุบัน</p>
					<p className="font-medium">
						{formatCreditsAmount(
							Math.max(0, calculation.totalReturnLossAmount),
						)}{' '}
						เครดิต
					</p>
				</div>
				<div className="rounded-lg border bg-muted/30 p-3">
					<p className="text-muted-foreground">รางวัลที่คาดในรอบนี้</p>
					<p className="font-medium text-primary">
						{formatCreditsAmount(Math.max(0, calculation.returnAmount ?? 0))}{' '}
						เครดิต
					</p>
				</div>
			</div>

			{info.isActive && info.minimumLossAmount > 0 && (
				<div className="mb-6 space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-muted-foreground">
							ความคืบหน้าเทียบยอดขั้นต่ำ (
							{formatCreditsAmount(info.minimumLossAmount)} เครดิต)
						</span>
						<span>
							{progressPct.toLocaleString('th-TH', {
								minimumFractionDigits: 1,
								maximumFractionDigits: 1,
							})}
							%
						</span>
					</div>
					<Progress value={progressPct} className="h-2" />
					{!calculation.canReceiveReturnLoss && (
						<p className="text-xs text-muted-foreground">
							ต้องมียอดเสียรวมถึงขั้นต่ำจึงจะได้รับคืนในรอบนี้
						</p>
					)}
				</div>
			)}

			<p className="mb-2 text-sm text-muted-foreground">
				เทิร์นที่ต้องทำก่อนโอน: {info.turnoverRound} เท่า
			</p>

			<h3 className="mb-3 text-lg font-semibold">ตามประเภทเกม</h3>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				{creditEntries.map(([gameType, ratio]) => {
					const loss = calculation.totalReturnLossByGameType?.[gameType] ?? 0
					const ret = calculation.returnAmountByGameType?.[gameType] ?? 0
					const pct = (ratio.creditRatio ?? 0) * 100

					return (
						<Card key={gameType}>
							<CardContent className="p-4">
								<div className="flex flex-col gap-2">
									<div className="flex items-start justify-between gap-2">
										<p className="font-medium">
											{formatGameTypeToThai(gameType)}
										</p>
										<p className="text-sm font-bold text-primary">
											{pct.toLocaleString('th-TH', {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
											%
										</p>
									</div>
									{loss < 0 ? (
										<p className="text-xs text-muted-foreground">
											ยอดชนะรวม {formatCreditsAmount(Math.abs(loss))} เครดิต
										</p>
									) : (
										<p className="text-xs text-muted-foreground">
											ยอดเสียรวม {formatCreditsAmount(loss)} เครดิต
										</p>
									)}
									<p className="text-xs text-primary">
										รางวัลคืนยอดเสีย (ก่อนรวมทุกประเภท):{' '}
										<span className="font-semibold">
											{formatCreditsAmount(ret)}
										</span>{' '}
										เครดิต
									</p>
									{ratio.maxCreditAmount != null && (
										<p className="text-xs text-muted-foreground">
											สูงสุดต่อรอบ {formatCreditsAmount(ratio.maxCreditAmount)}{' '}
											เครดิต
										</p>
									)}
								</div>
							</CardContent>
						</Card>
					)
				})}
			</div>
		</div>
	)
}
